import React, { useState, useEffect, useMemo } from 'react';
import { Select, Input } from '@chakra-ui/react';
import { useSchemaQuery } from '../../react-apollo-introspection';
import { tableAtom } from './atoms';
import { useRecoilState } from 'recoil';
import debounce from 'lodash.debounce';
import { useSearchParams, useNavigate } from 'react-router-dom';

const CustomSearch = ({ filters }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialSearchValue = searchParams.get('googleplacesName') || '';

  const [inputValue, setInputValue] = useState(initialSearchValue);
  const [googleplaces, setGoogleplaces] = useState(null);
  const [tableState, setTableState] = useRecoilState(tableAtom);
  const [fetchTypes, { data }] = useSchemaQuery('googleplaces_getTypeOptions');

  useEffect(() => {
    fetchTypes();
  }, []);

  const debouncedUpdateSearch = useMemo(
    () =>
      debounce((value) => {
        // Update Recoil state
        setTableState((old) => ({
          ...old,
          regexp: value ? { googleplacesName: value } : undefined,
          params: {
            ...old.params,
            page: 1,
            skip: 0,
            pagination: 20,
          },
        }));

        // Update URL
        const updatedParams = new URLSearchParams(searchParams);
        updatedParams.set('page', '1');
        updatedParams.set('limit', '20');

        if (value) {
          updatedParams.set('googleplacesName', value);
        } else {
          updatedParams.delete('googleplacesName');
        }

        navigate(`?${updatedParams.toString()}`, { replace: true });
      }, 600),
    [searchParams, navigate, setTableState]
  );

  useEffect(() => {
    if (inputValue === '') {
      debouncedUpdateSearch.cancel();

      // Remove from Recoil
      setTableState((old) => {
        const oldRegexp = old.regexp || {};
        const newRegexp = { ...oldRegexp };
        delete newRegexp.googleplacesName;

        return {
          ...old,
          regexp: Object.keys(newRegexp).length > 0 ? newRegexp : undefined,
          params: {
            ...old.params,
            page: 1,
            skip: 0,
            pagination: 20,
          },
        };
      });

      // Remove from URL
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.delete('googleplacesName');
      navigate(`?${updatedParams.toString()}`, { replace: true });
    } else {
      debouncedUpdateSearch(inputValue);
    }

    return () => debouncedUpdateSearch.cancel();
  }, [inputValue]);

  const handleTypeChange = (e) => {
    const selected = e.target.value;
    if (selected === 'clear') {
      setGoogleplaces(null);
    } else {
      setGoogleplaces({ types: [selected] });
    }
  };

  return (
    <>
      <Input
        placeholder="Search by name"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
      />

      <Select onChange={handleTypeChange} value={googleplaces?.types?.[0] || 'clear'}>
        <option value="clear">Select one</option>
        {data?.googleplaces_getTypeOptions?.map((type) => (
          <option key={type} value={type}>
            {type}
          </option>
        ))}
      </Select>
    </>
  );
};

export default CustomSearch;
